// The list's status vocabulary and the rules for what each row offers. Pure: everything comes from the row JSON
// (see ManualDeliverySerializer) plus the viewer's role and the current time.
import { compactWindow } from "./manualDeliveryTime";

export const STATUS = {
  not_sent: { label: "Not sent", tone: "notsent" },
  booked: { label: "Booked", tone: "booked" },
  out_for_delivery: { label: "Out for delivery", tone: "out" },
  delivered: { label: "Delivered", tone: "delivered" },
  not_booked: { label: "Not booked", tone: "failed" },
  check_courier: { label: "Check courier", tone: "check" },
  cancelled: { label: "Cancelled", tone: "cancelled" },
};

export const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "attention", label: "Needs attention" },
  { value: "not_sent", label: "Not sent" },
  { value: "booked", label: "Booked" },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const CANCELLABLE = ["not_sent", "booked", "out_for_delivery", "not_booked"];

export function roleOf(user) {
  if (user && user.isAdmin) return "admin";
  if (user && user.isManager) return "manager";
  return "staff";
}

// Ends text with exactly one full stop ("confirmed." + " Retry" never reads "confirmed.. Retry").
export function sentence(text) {
  const trimmed = String(text || "")
    .trim()
    .replace(/\.+$/, "");
  if (!trimmed) return "";
  return /[!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

export function primaryAction(row) {
  switch (row.display_status) {
    case "not_sent":
      return row.own_driver
        ? { label: "Send out", action: "send_out" }
        : { label: "Book", action: "book" };
    case "booked":
      return row.labels ? { label: "Label", action: "label" } : null;
    case "out_for_delivery":
      return { label: "Delivered", action: "delivered" };
    case "not_booked":
      return { label: "Retry", action: "book" };
    case "check_courier":
      return { label: "Check", action: "check" };
    default:
      return null;
  }
}

// Minutes until a manager may clear the attempt (0 when now), from the server's clearable_at; null when the
// server sent no clearable time (not available, never "now").
export function clearMinutesLeft(row, nowMs) {
  const at = row.attempt ? Date.parse(row.attempt.clearable_at || "") : NaN;
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - nowMs) / 60000));
}

export function menuItems(row, role, nowMs) {
  const items = [];
  const add = (action, label, options = {}) =>
    items.push({ action, label, disabled: false, hint: "", danger: false, ...options });
  const status = row.display_status;

  if (status === "booked" && row.labels) add("label", "Print label");
  if (status === "booked") add("mark_out", "Mark out for delivery");
  if (status === "cancelled") add("not_sent", "Move back to not sent");
  add("copy", "Copy as new delivery");
  if (status === "check_courier" && role !== "staff") {
    const wait = clearMinutesLeft(row, nowMs);
    if (wait === null) {
      add("clear", "Clear stuck booking", { disabled: true, hint: "Not available yet" });
    } else if (wait > 0) {
      add("clear", "Clear stuck booking", {
        disabled: true,
        hint: `Available in ${wait} min, after checking ${row.carrier_name}`,
      });
    } else {
      add("clear", "Clear stuck booking", { danger: true });
    }
  }
  if (CANCELLABLE.includes(status)) add("cancel", "Cancel delivery", { danger: true });
  if (role === "admin") {
    if (row.deletable) {
      add("delete", "Delete delivery", { danger: true });
    } else {
      add("delete", "Delete delivery", {
        disabled: true,
        hint:
          status === "check_courier"
            ? "Resolve the courier check first"
            : "Cancel the courier booking first",
      });
    }
  }
  return items;
}

function minutesAgo(iso, nowMs) {
  const at = Date.parse(iso || "");
  if (Number.isNaN(at)) return "just now";
  const minutes = Math.floor((nowMs - at) / 60000);
  return minutes < 1 ? "just now" : `${minutes} min ago`;
}

// The single full-width note under a row with a problem: a bold label plus plain text.
export function issueNote(row, role, nowMs) {
  if (row.display_status === "not_booked") {
    return {
      tone: "failed",
      title: "Not booked.",
      text: `${sentence(
        (row.extra || {}).last_error || "The courier did not accept the booking"
      )} Retry, or use Copy as new delivery to change the details.`,
    };
  }
  if (row.display_status === "check_courier") {
    const attempt = row.attempt || {};
    const parts = [
      `${row.carrier_name} didn’t confirm the booking ${minutesAgo(
        attempt.started_at,
        nowMs
      )}.`,
      `Look for ${row.reference} in their dashboard before booking again.`,
    ];
    if (attempt.error) parts.push(`Details: ${sentence(attempt.error)}`);
    if (role === "staff") parts.push("A manager can clear the attempt once checked.");
    return { tone: "check", title: "We don’t know if this booked.", text: parts.join(" ") };
  }
  return null;
}

export function filterRows(rows, { query, status }) {
  const q = (query || "").trim().toLowerCase();
  return rows.filter((row) => {
    if (status === "attention" && !row.needs_attention) return false;
    if (status && status !== "all" && status !== "attention" && row.display_status !== status) {
      return false;
    }
    if (!q) return true;
    const shipment = row.shipment || {};
    const haystack = [
      row.customer_name,
      row.address_text,
      row.items_notes,
      shipment.pickup_notes,
      row.reference,
      row.courier_reference,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const byWindow = (a, b) =>
  compare(a.delivery_date, b.delivery_date) ||
  compare(a.start_time, b.start_time) ||
  a.id - b.id;

// The list's window column: the compact window, or "No date" for a row saved without a delivery date.
export function rowWindowText(row) {
  return row.delivery_date ? compactWindow(row.start_time, row.end_time) : "No date";
}

export function groupRows(rows, todayIso) {
  const sorted = [...rows].sort(byWindow);
  const attention = sorted.filter((r) => r.needs_attention);
  const rest = sorted.filter((r) => !r.needs_attention);
  const past = rest
    .filter((r) => r.delivery_date && r.delivery_date < todayIso)
    .sort(
      (a, b) =>
        compare(b.delivery_date, a.delivery_date) ||
        compare(a.start_time, b.start_time) ||
        a.id - b.id
    );
  const groups = [];
  if (attention.length) {
    groups.push({
      key: "attention",
      title: "Needs attention",
      sub: "Not booked or waiting on a courier check",
      emptyText: "",
      rows: attention,
    });
  }
  groups.push({
    key: "today",
    title: "Today",
    sub: "",
    emptyText: "Nothing else scheduled for today.",
    rows: rest.filter((r) => r.delivery_date === todayIso),
  });
  groups.push({
    key: "upcoming",
    title: "Upcoming",
    sub: "",
    emptyText: "No upcoming deliveries in this date range.",
    // A row with no date is never dropped: it waits under Upcoming (rowWindowText says "No date").
    rows: rest.filter((r) => !r.delivery_date || r.delivery_date > todayIso),
  });
  if (past.length) {
    groups.push({ key: "past", title: "Past", sub: "Most recent first", emptyText: "", rows: past });
  }
  return groups;
}
